<template>
  <v-expansion-panel class="extraction-field">
    <extraction-field-header
      :required="field.required"
      :complete="hasValue"
    >
      {{ field.title }}
    </extraction-field-header>
    <v-expansion-panel-content class="content px-4 pb-4">
      <div
        v-if="hasDescription"
        class="text-body-2 font-weight-light grey--text text--darken-2 pt-4"
      >
        {{ field.description }}
      </div>
      <component
        :is="fieldComponent"
        v-if="fieldComponent"
        v-model="model"
        v-bind="componentProps"
      />
      <div v-else>Unknown field type</div>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import ExtractionFieldHeader from '@/components/DataExtraction/ExtractionFieldHeader';
import ExtractionFieldText from '@/components/DataExtraction/ExtractionFieldText';
import ExtractionFieldSingleSelect from '@/components/DataExtraction/ExtractionFieldSingleSelect';
import ExtractionFieldMultipleSelect from '@/components/DataExtraction/ExtractionFieldMultipleSelect';
import {
  DataExtractionFieldBase,
  DataExtractionFieldSingleSelect,
  DataExtractionFieldMultipleSelect,
  DataExtractionFieldText,
  DataExtractionValueBase,
} from '@/models';

export default {
  name: 'ExtractionField',
  components: {
    ExtractionFieldHeader,
    ExtractionFieldText,
    ExtractionFieldSingleSelect,
    ExtractionFieldMultipleSelect,
  },
  props: {
    value: {
      type: DataExtractionValueBase,
      default: null,
    },
    field: {
      type: DataExtractionFieldBase,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    reportId: {
      type: String,
      default: null,
    },
  },
  emits: ['input'],
  data: () => ({ internalValue: null }),
  computed: {
    fieldComponent() {
      if (this.field instanceof DataExtractionFieldText) {
        return 'ExtractionFieldText';
      } else if (
        this.field instanceof DataExtractionFieldSingleSelect
      ) {
        return 'ExtractionFieldSingleSelect';
      } else if (
        this.field instanceof DataExtractionFieldMultipleSelect
      ) {
        return 'ExtractionFieldMultipleSelect';
      }
      return '';
    },
    componentProps() {
      const props = {
        label: this.field.title,
        disabled: this.disabled,
        readonly: this.readonly,
      };
      switch (this.fieldComponent) {
        case 'ExtractionFieldSingleSelect':
        case 'ExtractionFieldMultipleSelect':
          props.options = this.field.multipleChoiceOptions;
          props.allowOther = this.field.otherOption;
          break;
      }
      return props;
    },
    hasDescription() {
      return !!this.field.description;
    },
    model: {
      get: function () {
        if (this.internalValue !== null) {
          return this.internalValue;
        }
        if (this.isArrayType) {
          return this.value?.valueArr ?? [];
        }
        return this.value?.value ?? '';
      },
      set: function (value) {
        if (this.readonly || this.disabled) {
          return;
        }
        this.internalValue = value;

        const newFields = {};
        if (this.isArrayType) {
          newFields.valueArr = value;
        } else {
          newFields.value = value;
        }
        this.$emit('input', newFields);
      },
    },
    isArrayType() {
      return this.field instanceof DataExtractionFieldMultipleSelect;
    },
    hasValue() {
      return this.model?.length > 0;
    },
  },
  watch: {
    field() {
      this.internalValue = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.extraction-field::before,
.extraction-field::after {
  display: none;
}
.extraction-field.v-expansion-panel--active
  > .v-expansion-panel-header {
  min-height: 0px !important;
}
</style>

<style lang="scss">
.content .v-expansion-panel-content__wrap {
  padding: 0px !important;
}
</style>
